/**
 * Live Pre-Order Margin Preview & Presentation Helpers.
 *
 * Constructs pre-order margin check payloads for the executing broker (Flattrade),
 * parses margin responses, and formats security/balance annotations for confirmation modals.
 */

export interface MarginPreviewParams {
  exchange: string;
  tradingsymbol: string;
  qty: number;
  price: number;
  side: string;
  product: string;
  order_type: string;
  trigger_price?: number;
}

export interface MarginPreviewResult {
  total: number;
  cash: number;
  remarks: string;
}

/**
 * Builds standard POST payload for broker pre-order margin evaluation.
 */
export function buildMarginPreviewPayload(
  params: MarginPreviewParams,
): Record<string, any> {
  const payload: Record<string, any> = {
    exchange: params.exchange,
    tradingsymbol: params.tradingsymbol,
    qty: params.qty,
    price: params.price,
    side: params.side,
    product: params.product,
    order_type: params.order_type,
  };

  if (params.trigger_price) {
    payload.trigger_price = params.trigger_price;
  }

  return payload;
}

/**
 * Parses broker margin response into numeric requirements and balance remarks.
 */
export function parseMarginPreviewResponse(
  data: any,
): MarginPreviewResult | null {
  if (!data || typeof data !== "object") return null;

  const m = data.margin || data;
  const total = parseFloat(m.ordermargin || m.marginused);
  if (!total || isNaN(total)) return null;

  return {
    total,
    cash: parseFloat(m.cash || 0) || 0,
    remarks: m.remarks || "",
  };
}

/**
 * Formats margin line HTML with warning badges when funds are insufficient.
 */
export function formatMarginPreviewHtml(
  m: MarginPreviewResult | null,
  escapeHtml: (s: string) => string,
): string {
  if (!m) return "";

  let marginLine = `<br>Margin required: <b>₹${m.total.toLocaleString("en-IN")}</b> <span style="color:var(--muted);">(flattrade)</span>`;

  if (/insufficient/i.test(m.remarks)) {
    marginLine += `<br><span style="color:var(--red);font-weight:700;">⚠ Broker: ${escapeHtml(m.remarks)} (cash ₹${m.cash.toLocaleString("en-IN")})</span>`;
  }

  return marginLine;
}

/**
 * Computes approximate nominal order turnover value.
 */
export function computeOrderApproxValue(qty: number, price: number): number {
  if (!qty || !price || isNaN(qty) || isNaN(price)) return 0;
  return qty * price;
}
